import { RSRP, SignalQualityTriangle } from '@bifravst/rsrp-bar'
import {
	EarthIcon,
	SatelliteIcon,
	Signal,
	SignalHigh,
	SignalLow,
	SignalMedium,
	SignalZero,
	Slash,
	type LucideIcon,
} from 'lucide-preact'
import { styled } from 'styled-components'
import type { Device } from './DeviceType.ts'
import { EnergyEstimate } from './DeviceType.ts'
import { LTEm } from './icons/LTE-m.js'
import { LTE } from './icons/LTE.tsx'
import { NBIot } from './icons/NBIot.js'

const EnergyEstimateIcons: Record<EnergyEstimate, LucideIcon> = {
	[EnergyEstimate.Bad]: SignalZero,
	[EnergyEstimate.Poor]: SignalLow,
	[EnergyEstimate.Normal]: SignalMedium,
	[EnergyEstimate.Good]: SignalHigh,
	[EnergyEstimate.Excellent]: Signal,
} as const

const EnergyEstimateDescription: Record<EnergyEstimate, string> = {
	[EnergyEstimate.Bad]:
		'Bad conditions. Difficulties in setting up connections. Maximum number of repetitions might be needed for data.',
	[EnergyEstimate.Poor]:
		'Poor conditions. Setting up a connection might require retries and a higher number of repetitions for data.',
	[EnergyEstimate.Normal]:
		'Normal conditions for cIoT device. No repetitions for data or only a few repetitions in the worst case.',
	[EnergyEstimate.Good]:
		'Good conditions. Possibly very good conditions for small amounts of data.',
	[EnergyEstimate.Excellent]:
		'Excellent conditions. Efficient data transfer estimated also for larger amounts of data.',
} as const

const EnergyEstimateLabel: Record<EnergyEstimate, string> = {
	[EnergyEstimate.Bad]: 'Bad',
	[EnergyEstimate.Poor]: 'Poor',
	[EnergyEstimate.Normal]: 'Normal',
	[EnergyEstimate.Good]: 'Good',
	[EnergyEstimate.Excellent]: 'Excellent',
} as const

const EestLabel = styled.abbr`
	margin-left: 0.5rem;
	&:before {
		content: '(';
	}
	&:after {
		content: ')';
	}
	font-size: 80%;
`

const StyledSignalQualityTriangle = styled(SignalQualityTriangle)`
	margin-right: calc(1rem + 4px);
	margin-left: 4px;
	width: 20px;
	height: 20px;
`

export const SignalQuality = ({ device }: { device: Device }) => {
	const { rsrp: rsrpDbm, eest } = device.state?.roam?.v ?? {}

	if (rsrpDbm === undefined && eest === undefined) return null

	if (eest !== undefined) {
		const SignalIcon = EnergyEstimateIcons[eest] ?? Slash
		return (
			<>
				<dt>
					<abbr
						title={EnergyEstimateDescription[eest] ?? 'Unknown Energy Estimate'}
					>
						<SignalIcon strokeWidth={2} />
					</abbr>
				</dt>
				<dd>
					<ConnectionTechnology device={device} />
					{rsrpDbm ?? '?'} dBm
					{eest !== undefined && (
						<EestLabel
							title={
								EnergyEstimateDescription[eest] ?? 'Unknown Energy Estimate'
							}
						>
							{EnergyEstimateLabel[eest] ?? 'unknown'}
						</EestLabel>
					)}
				</dd>
			</>
		)
	}

	return (
		<>
			<dt>
				{rsrpDbm !== undefined && (
					<RSRP
						dbm={rsrpDbm}
						renderBar={({ quality }) => (
							<StyledSignalQualityTriangle
								quality={quality}
								color={'inherit'}
								class="rsrp"
							/>
						)}
						renderInvalid={() => <SignalZero strokeWidth={2} />}
					/>
				)}
			</dt>
			<dd>
				<ConnectionTechnology device={device} />
				{rsrpDbm ?? '?'} dBm
			</dd>
		</>
	)
}

const Abbr = styled.abbr`
	svg {
		width: auto;
		height: 24px;
		margin-right: 0.5rem;
	}
`

const ConnectionTechnology = ({ device }: { device: Device }) => {
	const { nw } = device.state?.roam?.v ?? {}
	if (nw?.toLowerCase().includes('ntn') ?? false) return <NTN />
	return <NetworkInfo device={device} />
}

const NetworkInfo = ({ device }: { device: Device }) => {
	const { nw, band } = device.state?.roam?.v ?? {}
	return (
		<Abbr title={`Band ${band}`} class="me-2">
			<NetworkTechnologyIcon nw={nw} />
		</Abbr>
	)
}

const NetworkTechnologyIcon = ({ nw }: { nw?: string }) => {
	if (nw === 'LTE') return <LTE />
	if (nw === 'LTE-C1B') return <LTE />
	if (nw?.includes('NB-IoT') ?? false) return <NBIot />
	return <LTEm />
}

const NTN = () => (
	<span style={{ marginRight: '0.5rem' }}>
		<EarthIcon />
		<SatelliteIcon />
	</span>
)
